import {
  getAuth,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { app } from "../firebase";
import router from "../router.js";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  collection,
} from "firebase/firestore";

const auth = getAuth(app);
const db = getFirestore(app);

const ACTIVE_KID_STORAGE_PREFIX = "activeKidId";

function canUseSessionStorage() {
  return (
    typeof window !== "undefined" &&
    typeof window.sessionStorage !== "undefined"
  );
}

function activeKidStorageKey(uid) {
  return `${ACTIVE_KID_STORAGE_PREFIX}:${uid || "anonymous"}`;
}

function loadActiveKidId(uid) {
  if (!canUseSessionStorage() || !uid) return "";

  try {
    return window.sessionStorage.getItem(activeKidStorageKey(uid)) || "";
  } catch {
    return "";
  }
}

function saveActiveKidId(uid, kidId) {
  if (!canUseSessionStorage() || !uid) return;

  try {
    if (kidId) {
      window.sessionStorage.setItem(activeKidStorageKey(uid), kidId);
    } else {
      window.sessionStorage.removeItem(activeKidStorageKey(uid));
    }
  } catch {
    // ignore storage errors
  }
}

function clearActiveKidId(uid) {
  if (!canUseSessionStorage() || !uid) return;

  try {
    window.sessionStorage.removeItem(activeKidStorageKey(uid));
  } catch {
    // ignore storage errors
  }
}

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function cleanString(value) {
  return typeof value === "string" ? value.trim() : "";
}

function cleanNullableString(value) {
  const cleaned = cleanString(value);
  return cleaned || null;
}

function pad2(value) {
  return String(value).padStart(2, "0");
}

function formatDateToYMD(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate()
  )}`;
}

function normalizeActiveUntil(value) {
  if (!value) return "";

  if (typeof value?.toDate === "function") {
    return formatDateToYMD(value.toDate());
  }

  if (value instanceof Date) {
    return formatDateToYMD(value);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }

    const parsed = new Date(trimmed);
    return formatDateToYMD(parsed);
  }

  return "";
}

function sortKidEntries(a, b) {
  return String(a[0]).localeCompare(String(b[0]), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function normalizeKid(rawKid, slotKey, rootData = {}, user = null) {
  const src = isPlainObject(rawKid) ? rawKid : {};
  const root = isPlainObject(rootData) ? rootData : {};

  const kidId =
    cleanString(src.kidId) ||
    (String(slotKey) === "0" ? cleanString(root.kidId) : "") ||
    "";

  return {
    slotKey: String(slotKey),
    parentName: cleanString(src.parentName) || cleanString(root.parentName),
    kidName: cleanString(src.kidName) || cleanString(root.kidName),
    kidBirthDate:
      cleanString(src.kidBirthDate) || cleanString(root.kidBirthDate),
    activeUntil: normalizeActiveUntil(src.activeUntil ?? root.activeUntil),
    groupId: cleanString(src.groupId) || cleanString(root.groupId),
    schoolId: cleanString(src.schoolId) || cleanString(root.schoolId),
    teacherId: cleanString(src.teacherId) || cleanString(root.teacherId),
    kidId,
    userId: cleanString(user?.uid),
    email: cleanString(root.email) || cleanString(user?.email),
  };
}

function normalizeKids(rawKids, rootData = {}, user = null) {
  let entries = [];

  if (Array.isArray(rawKids)) {
    entries = rawKids.map((kid, index) => [String(index), kid]);
  } else if (isPlainObject(rawKids)) {
    entries = Object.entries(rawKids).sort(sortKidEntries);
  }

  const kids = entries
    .map(([slotKey, kid]) => normalizeKid(kid, slotKey, rootData, user))
    .filter((kid) => kid.kidId || kid.kidName);

  if (kids.length > 0) {
    return kids;
  }

  const legacyKid = normalizeKid({}, "0", rootData, user);
  return legacyKid.kidId || legacyKid.kidName ? [legacyKid] : [];
}

function applySelectedKid(userData, preferredKidId = "") {
  const kids = Array.isArray(userData?.kids) ? userData.kids : [];
  const safePreferredKidId = cleanString(preferredKidId);

  const activeKid =
    kids.find((kid) => kid.kidId === safePreferredKidId) || kids[0] || null;

  const hasAccountActiveUntil = Object.prototype.hasOwnProperty.call(
    userData || {},
    "accountActiveUntil"
  );

  const accountActiveUntil = normalizeActiveUntil(
    hasAccountActiveUntil ? userData?.accountActiveUntil : userData?.activeUntil
  );

  return {
    ...userData,
    kids,
    accountActiveUntil,
    activeKidId: activeKid?.kidId || "",
    activeKidIndex: activeKid ? kids.indexOf(activeKid) : -1,
    activeKid: activeKid || null,

    // backward-compatible shallow fields
    parentName: activeKid?.parentName || cleanString(userData?.parentName),
    kidName: activeKid?.kidName || "",
    kidBirthDate: activeKid?.kidBirthDate || "",
    activeUntil: activeKid?.activeUntil || accountActiveUntil || "",
    groupId: activeKid?.groupId || "",
    schoolId: activeKid?.schoolId || "",
    teacherId: activeKid?.teacherId || "",
    kidId: activeKid?.kidId || "",
  };
}

function normalizeUserData(user, rawUserDoc, preferredKidId = "") {
  const src = isPlainObject(rawUserDoc) ? rawUserDoc : {};
  const kids = normalizeKids(src.kids, src, user);

  const base = {
    ...src,
    email: cleanString(src.email) || cleanString(user?.email),
    parentName: cleanString(src.parentName),
    role: cleanString(src.role),
    teacherName: cleanString(src.teacherName),
    accountActiveUntil: normalizeActiveUntil(
      src.accountActiveUntil ?? src.activeUntil
    ),
    kids,
  };

  return applySelectedKid(base, preferredKidId);
}

async function fetchNormalizedUserData(user) {
  const userDocRef = doc(db, "users", user.uid);
  const userDocSnap = await getDoc(userDocRef);
  const preferredKidId = loadActiveKidId(user.uid);

  if (!userDocSnap.exists()) {
    return normalizeUserData(user, {}, preferredKidId);
  }

  return normalizeUserData(user, userDocSnap.data(), preferredKidId);
}

async function syncLevelSessionsBackend(dispatch, user, userData) {
  const kidId = cleanString(userData?.activeKidId || userData?.kidId);

  await dispatch(
    "levelSessions/configureBackend",
    {
      firestore: {
        enabled: !!user?.uid && !!kidId,
        db,
        uid: user?.uid || "",
        kidId: kidId || "",
        writeRawSessions: false,
      },
    },
    { root: true }
  );
}

const state = () => ({
  user: null,
  userData: null,
  authReady: false,
});

const getters = {
  loggedInUser: (state) => state.user,
  userData: (state) => state.userData,
  kids: (state) => state.userData?.kids || [],
  activeKid: (state) => state.userData?.activeKid || null,
};

const actions = {
  async signup({ commit, dispatch }, payload) {
    try {
      const email = cleanString(payload?.email);
      const password = payload?.password || "";
      const parentName = cleanString(payload?.parentName);
      const kidName = cleanString(payload?.kidName);
      const kidBirthDate = cleanString(payload?.kidBirthDate);

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      const user = userCredential.user;
      const kidId = doc(collection(db, "_generatedIds")).id;

      const userDocData = {
        email,
        parentName,
        kids: {
          0: {
            kidName,
            kidBirthDate,
            activeUntil: null,
            groupId: cleanNullableString(payload?.groupId),
            schoolId: cleanNullableString(payload?.schoolId),
            teacherId: cleanNullableString(payload?.teacherId),
            kidId,
          },
        },
      };

      await setDoc(doc(db, "users", user.uid), userDocData);

      const normalized = normalizeUserData(user, userDocData, kidId);

      commit("setUser", user);
      commit("setUserData", normalized);
      saveActiveKidId(user.uid, normalized.activeKidId);

      await syncLevelSessionsBackend(dispatch, user, normalized);

      router.push("/");
      return userCredential;
    } catch (error) {
      let errorMessage = "";

      switch (error?.code) {
        case "auth/email-already-in-use":
          errorMessage = "El correo electrónico ya está en uso.";
          break;
        case "auth/invalid-email":
          errorMessage = "Formato de correo electrónico inválido.";
          break;
        case "auth/weak-password":
          errorMessage = "La contraseña es demasiado débil.";
          break;
        default:
          errorMessage = "Ocurrió un error. Por favor, inténtalo de nuevo.";
      }

      throw errorMessage;
    }
  },

  async login({ commit, dispatch }, payload) {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        cleanString(payload?.email),
        payload?.password || ""
      );

      const user = userCredential.user;
      const normalized = await fetchNormalizedUserData(user);

      commit("setUser", user);
      commit("setUserData", normalized);
      saveActiveKidId(user.uid, normalized.activeKidId);

      await syncLevelSessionsBackend(dispatch, user, normalized);

      router.push("/");
      return userCredential;
    } catch (error) {
      let errorMessage = "";

      switch (error?.code) {
        case "auth/user-not-found":
          errorMessage =
            "Usuario no encontrado. Por favor, verifica tu correo electrónico.";
          break;
        case "auth/wrong-password":
        case "auth/invalid-credential":
        case "auth/invalid-login-credentials":
          errorMessage =
            "Correo o contraseña incorrectos. Por favor, inténtalo de nuevo.";
          break;
        default:
          errorMessage = "Ocurrió un error. Por favor, inténtalo de nuevo.";
      }

      throw errorMessage;
    }
  },

  async selectActiveKid({ state, commit, dispatch }, kidId) {
    const currentUser = state.user;
    const currentUserData = state.userData;

    if (!currentUser || !currentUserData) return;

    const nextUserData = applySelectedKid(currentUserData, kidId);

    if (!nextUserData.activeKidId) return;
    if (nextUserData.activeKidId === currentUserData.activeKidId) return;

    await dispatch("levelSessions/finalizeIfNeeded", null, { root: true });

    commit("setUserData", nextUserData);
    saveActiveKidId(currentUser.uid, nextUserData.activeKidId);

    await syncLevelSessionsBackend(dispatch, currentUser, nextUserData);
  },

  async logout({ state, commit, dispatch }) {
    const uid = state.user?.uid || "";

    await dispatch("levelSessions/finalizeIfNeeded", null, { root: true });
    await signOut(auth);

    if (uid) {
      clearActiveKidId(uid);
    }

    commit("setUser", null);
    commit("setUserData", null);

    await dispatch(
      "levelSessions/configureBackend",
      { firestore: { enabled: false } },
      { root: true }
    );
  },

  initAuthentication({ commit, dispatch }) {
    return new Promise((resolve) => {
      let resolvedOnce = false;

      onAuthStateChanged(auth, async (user) => {
        try {
          if (user) {
            const normalized = await fetchNormalizedUserData(user);

            commit("setUser", user);
            commit("setUserData", normalized);
            saveActiveKidId(user.uid, normalized.activeKidId);

            await syncLevelSessionsBackend(dispatch, user, normalized);
          } else {
            commit("setUser", null);
            commit("setUserData", null);

            await dispatch(
              "levelSessions/configureBackend",
              { firestore: { enabled: false } },
              { root: true }
            );
          }
        } catch (error) {
          console.error("[auth] initAuthentication error:", error);
        } finally {
          commit("setAuthReady", true);

          if (!resolvedOnce) {
            resolvedOnce = true;
            resolve(user || null);
          }
        }
      });
    });
  },
};

const mutations = {
  setUser(state, user) {
    state.user = user;
  },
  setUserData(state, data) {
    state.userData = data;
  },
  setAuthReady(state, status) {
    state.authReady = !!status;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};